/**
 * «Начни отсюда»: карточка песочницы новичка. На карте заменяет уроки,
 * пока ученик на A1 не освоил базовые темы справочника; на главной —
 * зовёт туда же; в справочнике — показывает, сколько осталось.
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGuideStore, type SandboxState } from '../../store/guideStore';
import { Button } from '../Button';
import { GlowProgress } from '../GlowProgress';
import { IconCheck, IconLock } from '../icons';

interface SandboxCardProps {
  readonly sandbox: SandboxState;
  /** `gate` — вместо уроков на карте; `invite` — на главной; `tracker` — в справочнике. */
  readonly variant: 'gate' | 'invite' | 'tracker';
}

export function SandboxCard({ sandbox, variant }: SandboxCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const passSandbox = useGuideStore((state) => state.passSandbox);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      aria-label={t('guide.sandbox.title')}
      className="glass relative flex flex-col gap-4 overflow-hidden rounded-card border border-accent/40 p-6 shadow-card-lg sm:p-7"
    >
      <span aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-accent/20 blur-3xl" />
      <span className="relative font-heading text-xs font-bold uppercase tracking-wide text-accent-dark">🌱 {t('guide.sandbox.badge')}</span>
      <h2 className="relative t-h1">{t('guide.sandbox.title')}</h2>
      <p className="relative text-sm leading-snug text-muted">
        {variant === 'gate' ? t('guide.sandbox.gate', { count: sandbox.required }) : t('guide.sandbox.text', { count: sandbox.required })}
      </p>
      <ul className="relative flex flex-wrap gap-2">
        {sandbox.topics.map((topic) => (
          <li key={topic.id}>
            <button
              type="button"
              onClick={() => navigate(`/guide/${topic.id}`)}
              className={[
                'flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-heading text-sm font-bold ring-1 transition',
                topic.mastered ? 'bg-teal/15 text-teal ring-teal/40' : 'bg-surface text-heading ring-border hover:ring-accent',
              ].join(' ')}
            >
              {topic.mastered && <IconCheck size={14} aria-hidden="true" />}
              {t(`guide.sandbox.topic.${topic.key}`)}
            </button>
          </li>
        ))}
      </ul>
      <GlowProgress value={sandbox.mastered} max={sandbox.required} label={t('guide.sandbox.progress', { done: Math.min(sandbox.mastered, sandbox.required), total: sandbox.required })} />
      {variant !== 'tracker' && (
        <Button fullWidth magnetic onClick={() => navigate('/guide')}>
          {t('guide.sandbox.start')}
        </Button>
      )}
      {variant === 'gate' && (
        <p className="relative flex items-center justify-center gap-1.5 text-xs text-muted">
          <IconLock size={12} aria-hidden="true" /> {t('guide.sandbox.locked')}
        </p>
      )}
      {variant !== 'invite' && (
        <button type="button" onClick={passSandbox} className="relative self-center text-xs text-muted underline-offset-2 hover:text-heading hover:underline">
          {t('guide.sandbox.skip')}
        </button>
      )}
    </motion.section>
  );
}
